import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import AddressAutocomplete from "./AddressAutocomplete";
import { api } from "../lib/api";

// Edit an unpaid booking. Only the logistics are editable — hours and package
// change the price, so those go through delete-and-rebook, which returns
// reserved credits correctly.
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const BookingEditDialog = ({ booking, onOpenChange, onSave }) => {
  const [when, setWhen] = useState(toLocalInput(booking.scheduled_at));
  const [transmission, setTransmission] = useState(booking.transmission || "auto");
  const [pickup, setPickup] = useState(booking.pickup_location || "");
  const [busy, setBusy] = useState(false);
  // Only offer gearboxes this instructor actually teaches — otherwise the
  // learner only discovers the mismatch when the server rejects the save.
  const [offered, setOffered] = useState(["auto", "manual"]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/trainers/${booking.trainer_id}`)
      .then(r => {
        const list = r.data?.transmissions;
        if (!cancelled && Array.isArray(list) && list.length) setOffered(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [booking.trainer_id]);

  const pickupOk = pickup.trim().length >= 6;
  const changed =
    when !== toLocalInput(booking.scheduled_at) ||
    transmission !== (booking.transmission || "auto") ||
    pickup.trim() !== (booking.pickup_location || "");

  const save = async () => {
    if (!pickupOk || !changed) return;
    setBusy(true);
    try {
      const payload = { transmission, pickup_location: pickup.trim() };
      // Send UTC — the input is in the learner's local zone.
      if (when) payload.scheduled_at = new Date(when).toISOString();
      await onSave(payload);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm" data-testid="booking-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit booking</DialogTitle>
          <DialogDescription>
            {booking.trainer_name ? `Lesson with ${booking.trainer_name}. ` : ""}
            To change the number of hours or switch package, delete this booking and book again — your credits come back automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Date &amp; time</Label>
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
                   className="rounded-sm" data-testid="edit-scheduled-at" />
            <div className="text-[11px] text-muted-foreground mt-1">
              Must fall inside your instructor's published availability.
            </div>
          </div>

          <div>
            <Label>Transmission</Label>
            <div className="flex gap-2 mt-1">
              {["auto", "manual"].map((t) => {
                const available = offered.includes(t);
                return (
                  <Button key={t} type="button" variant={transmission === t ? "default" : "outline"}
                          className="rounded-sm capitalize flex-1"
                          disabled={!available}
                          title={available ? undefined : "This instructor does not teach this transmission"}
                          onClick={() => setTransmission(t)} data-testid={`edit-transmission-${t}`}>
                    {t}{available ? "" : " · n/a"}
                  </Button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Pickup address</Label>
            <AddressAutocomplete value={pickup} onChange={setPickup} minLength={6}
                                 placeholder="e.g. 23 Market St, Toronto, ON"
                                 className="rounded-sm" testId="edit-pickup" />
            {!pickupOk && (
              <div className="text-[11px] text-destructive mt-1" data-testid="edit-pickup-error">
                Include the street, city and province.
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <span className="text-xs text-muted-foreground">
            Price stays ${Number(booking.amount || 0).toFixed(2)}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="edit-booking-cancel">Cancel</Button>
            <Button className="rounded-sm" disabled={busy || !pickupOk || !changed} onClick={save} data-testid="edit-booking-save">
              {busy ? "Saving…" : changed ? "Save changes" : "No changes yet"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingEditDialog;
